import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../context/ThemeContext';

/**
 * Tooltip - Bulle de bande dessinée pour messages d'aide/info
 * Ne s'affiche que sur desktop (hover souris), pas sur mobile (touch)
 *
 * @param {string} text - Texte à afficher dans la bulle
 * @param {string} position - Position de la bulle: 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right' | 'left' | 'right' (défaut: 'top')
 * @param {boolean} show - Afficher/masquer la bulle (mode contrôlé)
 * @param {boolean} showOnHover - Afficher au survol (défaut: true si show non défini)
 * @param {number} maxWidth - Largeur max en px (défaut: 200)
 * @param {React.ReactNode} children - Élément déclencheur
 */
const Tooltip = ({
    text,
    position = 'top',
    show: controlledShow,
    showOnHover = true,
    maxWidth = 200,
    children
}) => {
    // Couleur de fond dynamique venant du thème (primaryColor)
    const { currentTheme } = useTheme();
    const bgColor = currentTheme?.primaryColor || '#8b5cf6';
    const textColor = currentTheme?.textOnPrimary || 'white';
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const triggerRef = useRef(null);
    const isControlled = controlledShow !== undefined;
    // Ne jamais afficher sur les appareils tactiles (mobile)
    const isVisible = isTouchDevice ? false : (isControlled ? controlledShow : (showOnHover && isHovered));

    // Détecter si c'est un appareil tactile
    useEffect(() => {
        const checkTouch = () => {
            setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
        };
        checkTouch();
    }, []);

    // Calculer la position du tooltip par rapport à l'élément déclencheur (position fixed = viewport)
    useEffect(() => {
        if (isVisible && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();

            let top, left;

            switch (position) {
                case 'top':
                    top = rect.top - 8;
                    left = rect.left + rect.width / 2;
                    break;
                case 'top-left':
                    top = rect.top - 8;
                    left = rect.right;
                    break;
                case 'top-right':
                    top = rect.top - 8;
                    left = rect.left;
                    break;
                case 'bottom':
                    top = rect.bottom + 8;
                    left = rect.left + rect.width / 2;
                    break;
                case 'bottom-left':
                    top = rect.bottom + 8;
                    left = rect.right;
                    break;
                case 'bottom-right':
                    top = rect.bottom + 8;
                    left = rect.left;
                    break;
                case 'left':
                    top = rect.top + rect.height / 2;
                    left = rect.left - 8;
                    break;
                case 'right':
                    top = rect.top + rect.height / 2;
                    left = rect.right + 8;
                    break;
                default:
                    top = rect.top - 8;
                    left = rect.left + rect.width / 2;
            }

            setTooltipPosition({ top, left });
        }
    }, [isVisible, position]);

    // Styles de positionnement pour le portail (position fixe dans le viewport)
    const getPortalPositionStyles = () => {
        const base = {
            position: 'fixed',
            zIndex: 999999,
            whiteSpace: 'normal',
            maxWidth: `${maxWidth}px`,
            pointerEvents: 'none',
        };

        switch (position) {
            case 'top':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translate(-50%, -100%)',
                };
            case 'top-left':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translate(-100%, -100%)',
                };
            case 'top-right':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translateY(-100%)',
                };
            case 'bottom':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translateX(-50%)',
                };
            case 'bottom-left':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translateX(-100%)',
                };
            case 'bottom-right':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                };
            case 'left':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translate(-100%, -50%)',
                };
            case 'right':
                return {
                    ...base,
                    top: tooltipPosition.top,
                    left: tooltipPosition.left,
                    transform: 'translateY(-50%)',
                };
            default:
                return base;
        }
    };

    // Style de la flèche selon la direction
    const getArrowStyles = () => {
        const arrowSize = 6;
        const base = {
            position: 'absolute',
            width: 0,
            height: 0,
            borderStyle: 'solid',
        };

        switch (position) {
            case 'top':
                return {
                    ...base,
                    bottom: `-${arrowSize}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
                    borderColor: `${bgColor} transparent transparent transparent`,
                };
            case 'top-left':
                return {
                    ...base,
                    bottom: `-${arrowSize}px`,
                    right: '12px',
                    borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
                    borderColor: `${bgColor} transparent transparent transparent`,
                };
            case 'top-right':
                return {
                    ...base,
                    bottom: `-${arrowSize}px`,
                    left: '12px',
                    borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
                    borderColor: `${bgColor} transparent transparent transparent`,
                };
            case 'bottom':
                return {
                    ...base,
                    top: `-${arrowSize}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
                    borderColor: `transparent transparent ${bgColor} transparent`,
                };
            case 'bottom-left':
                return {
                    ...base,
                    top: `-${arrowSize}px`,
                    right: '12px',
                    borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
                    borderColor: `transparent transparent ${bgColor} transparent`,
                };
            case 'bottom-right':
                return {
                    ...base,
                    top: `-${arrowSize}px`,
                    left: '12px',
                    borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
                    borderColor: `transparent transparent ${bgColor} transparent`,
                };
            case 'left':
                return {
                    ...base,
                    right: `-${arrowSize}px`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
                    borderColor: `transparent transparent transparent ${bgColor}`,
                };
            case 'right':
                return {
                    ...base,
                    left: `-${arrowSize}px`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
                    borderColor: `transparent ${bgColor} transparent transparent`,
                };
            default:
                return base;
        }
    };

    // Rendu du tooltip via un portail pour éviter les problèmes de z-index
    const tooltipContent = isVisible && text && createPortal(
        <div
            style={getPortalPositionStyles()}
            className="animate-fade-in"
        >
            <div
                style={{
                    backgroundColor: bgColor,
                    color: textColor,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    fontSize: '13px',
                    lineHeight: '1.4',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
            >
                {text}
                <div style={getArrowStyles()} />
            </div>
        </div>,
        document.body
    );

    return (
        <div
            ref={triggerRef}
            /* flex-shrink-0 : ce wrapper ne doit jamais comprimer l'élément qu'il
               enveloppe. Sans ça, dans un conteneur flex à l'étroit (dock, barres
               d'actions), il s'écrase et l'enfant se retrouve à 0px de large. */
            className="relative inline-flex flex-shrink-0"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {children}
            {tooltipContent}
        </div>
    );
};

export default Tooltip;
